import React from 'react';

interface BlogImage {
  id: number;
  image_filename: string;
  image_alt_text?: string;
  sort_order: number;
}

interface BlogContentProps {
  content: string;
  contentImages?: BlogImage[];
}

// Function to format text (convert **bold** to <strong>bold</strong>, etc.)
const formatText = (text: string): React.ReactNode => {
  // Split by image placeholders and process each part
  const parts = text.split(/(\[IMAGE:[^\]]+\])/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('[IMAGE:') && part.endsWith(']')) {
      // This is an image placeholder - we'll handle this separately
      return part;
    }
    
    // Process text formatting
    let formattedText = part;
    const elements: React.ReactNode[] = [];
    let key = 0;
    
    // Handle bold text **text**
    const boldParts = formattedText.split(/(\*\*[^*]+\*\*)/g);
    boldParts.forEach((boldPart, boldIndex) => {
      if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
        elements.push(
          <strong key={`bold-${key++}`}>
            {boldPart.slice(2, -2)}
          </strong>
        );
      } else if (boldPart) {
        // Handle italic text *text* within this part
        const italicParts = boldPart.split(/(\*[^*]+\*)/g);
        italicParts.forEach((italicPart, italicIndex) => {
          if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
            elements.push(
              <em key={`italic-${key++}`}>
                {italicPart.slice(1, -1)}
              </em>
            );
          } else if (italicPart) {
            // Handle line breaks
            const lines = italicPart.split(/\n/g);
            lines.forEach((line, lineIndex) => {
              if (lineIndex > 0) {
                elements.push(<br key={`br-${key++}`} />);
              }
              if (line) {
                elements.push(line);
              }
            });
          }
        });
      }
    });
    
    return elements.length > 0 ? elements : part;
  });
};

// Function to clean HTML from content
const cleanHtmlContent = (text: string): string => {
  return text
    // Convert common HTML tags to plain text equivalents
    .replace(/<br\s*\/?>/gi, '\n')           // <br> → newline
    .replace(/<\/p>/gi, '\n\n')              // </p> → double newline
    .replace(/<p[^>]*>/gi, '')               // Remove <p> opening tags
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')  // <strong> → **bold**
    .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')            // <b> → **bold**
    .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')            // <em> → *italic*
    .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')              // <i> → *italic*
    .replace(/<li[^>]*>/gi, '\n- ')          // <li> → list item
    .replace(/<\/li>/gi, '')                 // Remove </li>
    .replace(/<ul[^>]*>/gi, '\n')            // <ul> → newline
    .replace(/<\/ul>/gi, '\n')               // </ul> → newline
    .replace(/<ol[^>]*>/gi, '\n')            // <ol> → newline
    .replace(/<\/ol>/gi, '\n')               // </ol> → newline
    .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n\n**$1**\n\n')  // Headings → bold
    .replace(/<[^>]+>/g, '')                 // Remove any remaining HTML tags
    .replace(/&nbsp;/g, ' ')                 // &nbsp; → space
    .replace(/&amp;/g, '&')                  // &amp; → &
    .replace(/&lt;/g, '<')                   // &lt; → <
    .replace(/&gt;/g, '>')                   // &gt; → >
    .replace(/&quot;/g, '"')                 // &quot; → "
    .replace(/&#39;/g, "'")                  // &#39; → '
    .replace(/\n{3,}/g, '\n\n')              // Multiple newlines → double newline
    .trim();
};

// Function to render blog content with images
export const renderBlogContent = ({ content, contentImages = [] }: BlogContentProps): React.ReactNode => {
  // Create a map of image placeholders to actual images
  const imageMap = new Map<string, BlogImage>();
  contentImages.forEach(img => {
    imageMap.set(`[IMAGE:${img.image_filename}]`, img);
  });
  
  // Process content to replace image placeholders with actual img tags
  let processedContent = content;
  
  // Replace image placeholders with actual img tags
  imageMap.forEach((image, placeholder) => {
    const imgTag = `<img src="/images/${image.image_filename}" alt="${image.image_alt_text || 'Blog content image'}" style="max-width: 100%; height: auto; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);" />`;
    processedContent = processedContent.replace(placeholder, imgTag);
  });
  
  // Remove any remaining unmatched image placeholders
  processedContent = processedContent.replace(/\[IMAGE:[^\]]+\]/g, '');
  
  // Render HTML content directly
  return (
    <div 
      style={{ lineHeight: '1.6', fontSize: '16px' }}
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

// Function to strip HTML tags and image placeholders for preview
export const stripContentForPreview = (content: string): string => {
  // First clean HTML, then strip formatting
  const htmlCleaned = cleanHtmlContent(content);
  
  return htmlCleaned
    .replace(/\[IMAGE:[^\]]+\]/g, '[Image]') // Replace image placeholders with [Image]
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold formatting
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic formatting
    .replace(/<[^>]+>/g, ' ') // Remove any remaining HTML tags
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

export default { renderBlogContent, stripContentForPreview };


